import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Upload, Trash2, ImageIcon, Save, X, GripVertical, Video } from "lucide-react";

const isVideoUrl = (u) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(u || "");

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [novoFoto, setNovoFoto] = useState(null); // {url, path, kind}
  const [novoObs, setNovoObs] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);
  const inputRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/portfolio");
      setItems(data);
    } catch {
      toast.error("Erro ao carregar portfólio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd);
      setNovoFoto({ url: data.url, path: data.path, kind: data.kind || (file.type.startsWith("video/") ? "video" : "image") });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar arquivo");
    } finally { setUploading(false); }
  };

  const cancelar = () => {
    setAdding(false);
    setNovoFoto(null);
    setNovoObs("");
  };

  const salvar = async () => {
    if (!novoFoto?.url) return toast.error("Envie uma foto ou vídeo antes de salvar");
    setSaving(true);
    try {
      await api.post("/portfolio", {
        foto: novoFoto.url,
        observacao: novoObs,
        ordem: items.length,
        tipo: novoFoto.kind === "video" ? "video" : "imagem",
      });
      toast.success(novoFoto.kind === "video" ? "Vídeo adicionado ao portfólio" : "Foto adicionada ao portfólio");
      cancelar();
      load();
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (item) => {
    if (!window.confirm("Excluir este item do portfólio?")) return;
    try {
      await api.delete(`/portfolio/${item.id}`);
      toast.success("Item removido");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const salvarObs = async (item, novaObs) => {
    if (novaObs === item.observacao) return;
    try {
      await api.put(`/portfolio/${item.id}`, {
        foto: item.foto,
        observacao: novaObs,
        ordem: item.ordem || 0,
      });
      toast.success("Observação atualizada");
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const persistirOrdem = async (novaLista) => {
    try {
      await api.put("/portfolio/reordenar", { ordem: novaLista.map((it) => it.id) });
    } catch {
      toast.error("Erro ao reordenar. Recarregando…");
      load();
    }
  };

  const onDragStart = (id) => setDraggingId(id);
  const onDragOver = (e, overIt) => {
    e.preventDefault();
    if (!draggingId || draggingId === overIt.id) return;
    setOverId(overIt.id);
  };
  const onDragLeave = () => setOverId(null);
  const onDrop = (e, targetIt) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetIt.id) {
      setDraggingId(null); setOverId(null); return;
    }
    const from = items.findIndex((i) => i.id === draggingId);
    const to = items.findIndex((i) => i.id === targetIt.id);
    if (from < 0 || to < 0) return;
    const novo = items.slice();
    const [moved] = novo.splice(from, 1);
    novo.splice(to, 0, moved);
    setItems(novo);
    setDraggingId(null);
    setOverId(null);
    persistirOrdem(novo);
  };
  const onDragEnd = () => { setDraggingId(null); setOverId(null); };

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
            Vitrine
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Portfólio de Serviços</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Fotos exibidas na página inicial para gerar confiança nos clientes.
          </p>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            data-testid="add-portfolio-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold self-start"
          >
            <Plus className="w-4 h-4" /> Adicionar foto
          </button>
        )}
      </div>

      {/* Form de adição */}
      {adding && (
        <div className="bbh-card p-4 space-y-3 max-w-2xl" data-testid="portfolio-form">
          <div className="flex items-start gap-3">
            <div className="w-20 h-20 rounded-md border border-zinc-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
              {novoFoto?.path ? (
                novoFoto.kind === "video" ? (
                  <video src={fileUrl(novoFoto.path)} className="w-full h-full object-cover" muted playsInline />
                ) : (
                  <img src={fileUrl(novoFoto.path)} alt="" className="w-full h-full object-cover" />
                )
              ) : (
                <ImageIcon className="w-7 h-7 text-zinc-300" />
              )}
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid="upload-portfolio-btn"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-zinc-200 text-xs font-medium hover:bg-zinc-50 disabled:opacity-60"
                disabled={uploading}
              >
                {novoFoto?.kind === "video" ? <Video className="w-3 h-3" /> : <Upload className="w-3 h-3" />}
                {uploading ? "Enviando..." : (novoFoto ? "Trocar arquivo" : "Enviar foto ou vídeo")}
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/webm"
                hidden
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
              />
              <textarea
                value={novoObs}
                onChange={(e) => setNovoObs(e.target.value)}
                rows={2}
                data-testid="portfolio-obs-input"
                placeholder="Observação (opcional) — ex.: Reforma de fachada"
                className="w-full px-2.5 py-1.5 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-zinc-100">
            <button
              onClick={cancelar}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-zinc-200 text-xs font-medium"
            >
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={saving || !novoFoto}
              data-testid="save-portfolio-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bbh-btn-primary text-xs font-semibold disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="text-zinc-500 text-sm">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="bbh-card p-8 text-center text-zinc-500 text-sm">
          Nenhuma foto no portfólio ainda. Adicione a primeira para exibi-la na página inicial.
        </div>
      ) : (
        <>
          <div className="text-xs text-zinc-500 -mb-1 flex items-center gap-1.5">
            <GripVertical className="w-3.5 h-3.5" />
            Arraste as fotos para reordenar como aparecem na página inicial
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {items.map((it) => (
              <PortfolioCard
                key={it.id}
                item={it}
                dragging={draggingId === it.id}
                over={overId === it.id}
                onDragStart={() => onDragStart(it.id)}
                onDragOver={(e) => onDragOver(e, it)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, it)}
                onDragEnd={onDragEnd}
                onDelete={() => excluir(it)}
                onSaveObs={(v) => salvarObs(it, v)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PortfolioCard({ item, dragging, over, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, onDelete, onSaveObs }) {
  const [obs, setObs] = useState(item.observacao || "");
  const path = (item.foto || "").replace("/api/files/", "");
  const isVideo = item.tipo === "video" || isVideoUrl(item.foto);
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(); }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`bbh-card overflow-hidden flex flex-col transition-all ${dragging ? "opacity-40" : ""} ${over ? "ring-2 ring-[#14243D] ring-offset-2" : ""}`}
      data-testid={`portfolio-card-${item.id}`}
    >
      <div className="relative aspect-square bg-zinc-50 overflow-hidden group cursor-move">
        {isVideo ? (
          <video src={fileUrl(path)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        ) : (
          <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" draggable={false} />
        )}
        {isVideo && (
          <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] uppercase tracking-widest font-bold">
            <Video className="w-2.5 h-2.5" /> Vídeo
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 px-1 py-0.5 rounded bg-black/60 text-white text-[9px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <GripVertical className="w-2.5 h-2.5" />
        </div>
        <button
          onClick={onDelete}
          data-testid={`portfolio-delete-${item.id}`}
          aria-label="Excluir"
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/85 hover:bg-white text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2">
        <textarea
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          onBlur={() => onSaveObs(obs)}
          rows={2}
          data-testid={`portfolio-card-obs-${item.id}`}
          placeholder="Observação..."
          className="w-full px-2 py-1 rounded border border-zinc-200 bbh-input text-[11px] leading-tight bg-white resize-none"
          onDragStart={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}
